// =============================================================================
// MeuDriver — StudentQuiz
// Simulado de questões de trânsito estilo Detran.
// Rota: /aluno/simulado
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import { BookOpen, CheckCircle2, XCircle, ChevronRight, RotateCcw, Loader2, Clock, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { useQuiz } from '@/hooks/useQuiz'
import type { QuizCategory } from '@/types/database'

const CATEGORY_LABELS: Record<string, string> = {
  sinalizacao: 'Sinalização',
  legislacao: 'Legislação',
  direcao_defensiva: 'Direção Defensiva',
  primeiros_socorros: 'Primeiros Socorros',
  mecanica: 'Mecânica',
}

const CATEGORY_COLORS: Record<string, string> = {
  sinalizacao: 'bg-blue-50 text-blue-700 border-blue-200',
  legislacao: 'bg-purple-50 text-purple-700 border-purple-200',
  direcao_defensiva: 'bg-teal-50 text-teal-700 border-teal-200',
  primeiros_socorros: 'bg-red-50 text-red-700 border-red-200',
  mecanica: 'bg-orange-50 text-orange-700 border-orange-200',
}

const DIFFICULTY_LABELS: Record<string, string> = {
  facil: 'Fácil',
  normal: 'Normal',
  dificil: 'Difícil',
}

const DIFFICULTY_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  facil: 'secondary',
  normal: 'default',
  dificil: 'destructive',
}

const TIMER_SECONDS = 30

export default function StudentQuiz() {
  const { state, questions, currentIndex, currentQuestion, answers, startQuiz, answerQuestion, nextQuestion, finishQuiz, isLoading } = useQuiz()

  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [timeLeft, setTimeLeft] = useState(TIMER_SECONDS)
  const [timerActive, setTimerActive] = useState(false)

  // Reset estado da questão atual quando muda
  useEffect(() => {
    setSelectedOption(null)
    setShowExplanation(false)
    setTimeLeft(TIMER_SECONDS)
    if (state === 'question') setTimerActive(true)
  }, [currentIndex, state])

  // Timer
  useEffect(() => {
    if (!timerActive || selectedOption !== null) return

    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(interval)
          // Tempo esgotado — responde errada
          handleAnswer('__timeout__')
          return 0
        }
        return t - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [timerActive, selectedOption]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = useCallback(async (optionId: string) => {
    if (selectedOption !== null) return
    setTimerActive(false)
    setSelectedOption(optionId)
    setShowExplanation(true)
    await answerQuestion(optionId)
  }, [selectedOption, answerQuestion])

  const handleNext = useCallback(async () => {
    const isLast = currentIndex === questions.length - 1
    if (isLast) {
      await finishQuiz()
    } else {
      nextQuestion()
    }
  }, [currentIndex, questions.length, finishQuiz, nextQuestion])

  // ── Estado: idle (seleção de categoria) ────────────────────────────────────

  if (state === 'idle') {
    return (
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 mx-auto">
            <BookOpen className="h-7 w-7 text-brand-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Simulado Detran</h1>
          <p className="text-sm text-muted-foreground">
            Pratique questões de múltipla escolha e se prepare para a prova teórica.
          </p>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Escolha uma categoria ou faça o simulado completo:</p>
          <Button
            className="w-full"
            onClick={() => startQuiz(null)}
            data-testid="start-quiz-all"
          >
            Simulado completo (todas as categorias)
          </Button>
          {(Object.entries(CATEGORY_LABELS) as [QuizCategory, string][]).map(([cat, label]) => (
            <Button
              key={cat}
              variant="outline"
              className={cn('w-full justify-start gap-2', CATEGORY_COLORS[cat])}
              onClick={() => startQuiz(cat)}
              data-testid={`start-quiz-${cat}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  // ── Estado: loading ─────────────────────────────────────────────────────────

  if (state === 'loading' || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        <p className="text-sm text-muted-foreground">Carregando questões...</p>
      </div>
    )
  }

  // ── Estado: result (resultado final) ───────────────────────────────────────

  if (state === 'result') {
    const correct = answers.filter((a) => a.isCorrect).length
    const total = answers.length
    const percent = total > 0 ? Math.round((correct / total) * 100) : 0
    const passed = percent >= 70

    return (
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div className="text-center space-y-3">
          <div className={cn(
            'inline-flex h-16 w-16 items-center justify-center rounded-full mx-auto',
            passed ? 'bg-green-100' : 'bg-red-100',
          )}>
            <Trophy className={cn('h-8 w-8', passed ? 'text-green-600' : 'text-red-600')} />
          </div>
          <h1 className="text-xl font-bold text-gray-900">
            {passed ? 'Parabéns! Você passou!' : 'Continue praticando!'}
          </h1>
          <p className="text-4xl font-extrabold text-gray-900">{percent}%</p>
          <p className="text-sm text-muted-foreground">
            {correct} de {total} questões corretas
          </p>
        </div>

        <Progress value={percent} className={cn('h-3', passed ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500')} />

        {/* Review de erros */}
        {answers.some((a) => !a.isCorrect) && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Questões que você errou:</h2>
            {answers.filter((a) => !a.isCorrect).map((a, i) => (
              <Card key={i} className="border-red-100">
                <CardContent className="pt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">{a.question.question}</p>
                  <p className="text-xs text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Correta: {(a.question.options as { id: string; text: string }[]).find((o) => o.id === a.question.correct_option)?.text}
                  </p>
                  {a.question.explanation && (
                    <p className="text-xs text-muted-foreground italic">{a.question.explanation}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Button className="w-full" onClick={() => startQuiz(null)} data-testid="restart-quiz-btn">
          <RotateCcw className="h-4 w-4 mr-2" />
          Fazer novo simulado
        </Button>
      </div>
    )
  }

  // ── Estado: question ────────────────────────────────────────────────────────

  if (!currentQuestion) return null

  const options = currentQuestion.options as { id: string; text: string }[]
  const progress = ((currentIndex + 1) / questions.length) * 100
  const timerPercent = (timeLeft / TIMER_SECONDS) * 100

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
      {/* Header com progresso e timer */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Questão {currentIndex + 1} de {questions.length}</span>
          <span className={cn(
            'flex items-center gap-1 font-medium',
            timeLeft <= 10 ? 'text-red-600' : 'text-gray-600',
          )}>
            <Clock className="h-3.5 w-3.5" />
            {timeLeft}s
          </span>
        </div>
        <Progress value={progress} className="h-1.5" />
        <Progress
          value={timerPercent}
          className={cn('h-1', timerPercent <= 33 ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-400')}
        />
      </div>

      {/* Categoria e dificuldade */}
      <div className="flex items-center gap-2">
        <span className={cn(
          'text-[11px] font-medium px-2 py-0.5 rounded-full border',
          CATEGORY_COLORS[currentQuestion.category] ?? 'bg-gray-50 text-gray-600 border-gray-200',
        )}>
          {CATEGORY_LABELS[currentQuestion.category] ?? currentQuestion.category}
        </span>
        <Badge variant={DIFFICULTY_VARIANTS[currentQuestion.difficulty] ?? 'default'} className="text-[10px]">
          {DIFFICULTY_LABELS[currentQuestion.difficulty] ?? currentQuestion.difficulty}
        </Badge>
      </div>

      {/* Pergunta */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold leading-snug text-gray-900">
            {currentQuestion.question}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {options.map((opt) => {
            const isSelected = selectedOption === opt.id
            const isCorrect = opt.id === currentQuestion.correct_option
            const isAnswered = selectedOption !== null

            let optionClass = 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
            if (isAnswered) {
              if (isCorrect) optionClass = 'border-green-400 bg-green-50 text-green-800'
              else if (isSelected) optionClass = 'border-red-400 bg-red-50 text-red-800'
              else optionClass = 'border-gray-200 bg-gray-50 text-gray-400'
            }

            return (
              <button
                key={opt.id}
                className={cn(
                  'w-full text-left text-sm rounded-lg border px-4 py-3 transition-all duration-150',
                  optionClass,
                  !isAnswered && 'cursor-pointer active:scale-[0.99]',
                  isAnswered && 'cursor-default',
                )}
                onClick={() => handleAnswer(opt.id)}
                disabled={isAnswered}
                data-testid={`quiz-option-${opt.id}`}
              >
                <span className="font-semibold mr-2">{opt.id.toUpperCase()}.</span>
                {opt.text}
                {isAnswered && isCorrect && (
                  <CheckCircle2 className="inline h-4 w-4 ml-2 text-green-600" />
                )}
                {isAnswered && isSelected && !isCorrect && (
                  <XCircle className="inline h-4 w-4 ml-2 text-red-600" />
                )}
              </button>
            )
          })}
        </CardContent>
      </Card>

      {/* Explicação */}
      {showExplanation && currentQuestion.explanation && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800">
          <p className="font-semibold mb-1">Explicação</p>
          <p className="text-xs leading-relaxed">{currentQuestion.explanation}</p>
        </div>
      )}

      {/* Botão avançar */}
      {selectedOption !== null && (
        <Button
          className="w-full"
          onClick={handleNext}
          data-testid="quiz-next-btn"
        >
          {currentIndex === questions.length - 1 ? 'Ver resultado' : 'Próxima questão'}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      )}
    </div>
  )
}
